import React from 'react'
import { type To } from 'react-router-dom'

import { DateRangeBaseOption } from '../main/components/timeframe-bar/daterange-select'
import { TimeframeBaseOption } from '../main/components/timeframe-bar/timeframe-select'
import { TIMEFRAME } from '../main/constants'
import { SpecificChartFunctionality } from './shared/chartLayoutConfig'

export enum SettingsTab {
  Chart = 'chart',
  Colors = 'colors',
}

export type ChartLayoutProps = {
  config: object
  onRegisterChartLayoutModel?: (chartLayout: any) => void
  shouldResize?: boolean
  parentElement?: HTMLElement
}

export type ChartLayoutHeaderProps = {
  model: any
  onSettingsOpen: () => void
  hasPublish: boolean
}

interface ChartsLib {
  FREE_DATE_RANGES: string[]
  ChartLayout: React.FC<ChartLayoutProps>
  ChartLayoutHeader: React.FC<ChartLayoutHeaderProps>
  ChartLayoutWithGlobalState: React.FC<ChartLayoutProps>

  ChartSettings: React.FC<{
    onClose: () => void
    onActiveSettingsTabChange?: (settingsComponent: SettingsTab) => void
    activeSettingsTab?: SettingsTab
  }>

  PreventClosingUnsavedChanges: React.FC<{
    onHide: () => void
    children: (props: { checkForUnsavedChangesAndClose: () => void }) => React.ReactElement
  }>

  useChartLayoutGlobalModel: () => {
    chartLayoutModel: any | null
    chartLayoutModelConfigSnapshot: Record<string, any> | null
    lastGlobalRerender: number
    setChartLayoutModel: (chartLayoutModel: any) => void
    getChartLayoutModelConfig: (props: { fallback: Record<string, any> }) => Record<string, any>
    forceGlobalRerender: () => void
    generateNewLayoutModelConfigSnapshot: () => void
  }

  StockDetailTimeframeBar: React.FC<{
    className?: string
    isDateRangeAvailable: boolean
    isPremium: boolean
    isLoadingOverride?: boolean
    isScrollIntoViewEnabled?: boolean
    onTimeframeSelect?: (timeframe: TimeframeBaseOption<TIMEFRAME>) => void
    onDateRangeSelect?: (timeframe: DateRangeBaseOption) => void
  }>

  Quote: {
    create(quote: any): any
    select: (query: (quote: any) => boolean) => any
    deleteAll: () => void
    findByAttribute: (attribute: string, value: any) => any
    getAll: (tickersAndTimeframes: any) => any
  }

  Settings: {
    MarketSentiment: any
    Indicator: any
    Chart: any
    Thumb: any
    Element: any
    Colors: string[]
    updateColors: (type: string, name: string, theme: 'light' | 'dark', value: string) => void
  }

  Utils: {
    dateFromUnixTimestamp: (date: string) => Date
  }

  convertTa: {
    getPanes: (taConfig: any) => any
  }

  getTimeFrameGroups: (isPremium?: boolean) => Array<{ label: string; timeframes: TimeframeBaseOption<TIMEFRAME>[] }>
  getDateRangeGroups: (isPremium?: boolean) => Array<{ label: string; dateRanges: DateRangeBaseOption[] }>

  useModelState: <Model>(
    model: Model | null,
    options: { watchProperties?: Array<keyof Model>; listenOnEvents?: string[] }
  ) => Model | null

  useFavoriteTimeframes: (isPremium?: boolean) => {
    favoriteTimeframes: string[]
    toggleFavoriteTimeframe: (timeframe: TIMEFRAME) => void
  }

  generateTimeframeGroups: (
    isPremium: boolean,
    generatePath: (timeframe: TimeframeBaseOption<TIMEFRAME>) => To
  ) => Array<{ label: string; timeframes: TimeframeBaseOption<TIMEFRAME>[] }>

  generateDateRangeGroups: (
    isPremium: boolean,
    generatePath: (timeframe: DateRangeBaseOption) => To
  ) => Array<{ label: string; dateRanges: DateRangeBaseOption[] }>

  generatePathForTimeframeDateRange: (props: {
    isPremium: boolean
    pathname: string
    timeframe: TIMEFRAME
    dateRange: string | null | undefined
    specificChartFunctionality: SpecificChartFunctionality
    freeTimeframes?: string[]
  }) => To
}

// @ts-ignore
const chartsLib = (window.FLibs.Charts as ChartsLib) ?? {}

export const FREE_DATE_RANGES = chartsLib.FREE_DATE_RANGES
export const ChartLayout = chartsLib.ChartLayout
export const ChartLayoutWithGlobalState = chartsLib.ChartLayoutWithGlobalState
export const ChartLayoutHeader = chartsLib.ChartLayoutHeader
export const ChartSettings = chartsLib.ChartSettings
export const PreventClosingUnsavedChanges = chartsLib.PreventClosingUnsavedChanges
export const useChartLayoutGlobalModel = chartsLib.useChartLayoutGlobalModel
export const StockDetailTimeframeBar = chartsLib.StockDetailTimeframeBar
export const Quote = chartsLib.Quote
export const Settings = chartsLib.Settings
export const Utils = chartsLib.Utils
export const convertTa = chartsLib.convertTa
export const getTimeFrameGroups = chartsLib.getTimeFrameGroups
export const getDateRangeGroups = chartsLib.getDateRangeGroups
export const useModelState = chartsLib.useModelState
export const useFavoriteTimeframes = chartsLib.useFavoriteTimeframes
export const generateTimeframeGroups = chartsLib.generateTimeframeGroups
export const generateDateRangeGroups = chartsLib.generateDateRangeGroups
export const generatePathForTimeframeDateRange = chartsLib.generatePathForTimeframeDateRange
